import { Box, ButtonBase, Container } from '@mui/material';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import Illustration from '../_assets/illustration.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <div className="relative isolate flex w-full flex-col overflow-hidden">
        <Background />

        <Container maxWidth="xl">
          <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        </Container>
      </div>

      <Disclaimer />
    </>
  );
};

const Background = () => (
  <div className="absolute inset-0 isolate -z-10 flex flex-col overflow-hidden bg-[#031839]"></div>
);

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="text-white">
      <div className="flex flex-col items-center xl:flex-row">
        <div className="flex-6 pt-8 pb-12 md:pt-16 xl:pb-20">
          <h1 className="mb-4 text-4xl leading-tight font-bold text-balance sm:text-5xl xl:text-6xl">
            Gjett om du kan få lavere rente med <span className="text-[#18A0FB]">Scoopr</span>
          </h1>
          <Box
            component="p"
            sx={{
              mb: { xs: 3, md: 4, lg: 3 },
              maxWidth: { xs: 370, sm: 600, md: 750, xl: 800 },
            }}
            className="typography-v1-h3 text-balance"
          >
            Gjett riktig og delta om å få slettet inntil{' '}
            <span className="text-[#FDC564]">500.000 kr </span>
            av din forbruks- og kredittkortgjeld.
          </Box>

          <p className="mb-4 typography-v1-h4 font-normal">
            For å delta i denne konkurransen må du søke om lavere rente via Scoopr.
          </p>

          <NumberedList
            sx={{ mb: 2 }}
            renderNumber={(props) => (
              <NumberCircle
                sx={{
                  fontSize: 14,
                  height: 20,
                  backgroundColor: '#FBFDFF',
                  color: '#031839',
                }}
                {...props}
              />
            )}
            renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
          >
            <NumberedListItem>Logg inn med BankID</NumberedListItem>
            <NumberedListItem>Gjett om scoopr kan gi deg lavere rente</NumberedListItem>
            <NumberedListItem>Søk om lavere rente og delta</NumberedListItem>
          </NumberedList>

          {ended && (
            <p className="mb-6 typography-v1-h4">
              <span>Denne trekningen er avsluttet. </span>
              <ButtonBase
                onClick={() => setIsOpen(true)}
                sx={{
                  color: '#74C6FD',
                  textDecoration: 'underline',
                  mb: 0.5,
                  fontWeight: 700,
                  ml: 1,
                }}
              >
                Se vinneren her
              </ButtonBase>
            </p>
          )}

          <Box sx={{ mb: 3 }}>
            <a
              href={routes.AUGUST_2025_COMPETITION_TERMS}
              style={{ color: 'inherit' }}
              className="underline"
              target="_blank"
            >
              <p className="typography-v1-body3">Vilkår og betingelser for deltagelse</p>
            </a>
          </Box>

          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('aug25_competition_cta_top_clicked');
            }}
          />

          <p className="mt-6 typography-v1-h4">
            <i>
              Scoopr samarbeider med flere banker for å se om vi kan skaffe deg lavere rente på din
              gjeld.{' '}
            </i>
          </p>

          {props.winners && (
            <CompetitionWinnersDialog
              open={isOpen}
              onClose={() => setIsOpen(false)}
              winners={props.winners}
            />
          )}
        </div>

        <div className="mt-auto min-w-0 flex-5 xl:flex">
          <div className="relative isolate max-xl:max-w-md xl:w-full">
            <Illustration className="h-auto w-full max-xl:[&_.eye-left]:translate-x-2.5 max-xl:[&_.eye-left]:-translate-y-1.5 max-xl:[&_.eye-right]:translate-x-2.5 max-xl:[&_.eye-right]:-translate-y-2.5 max-xl:[&_.text]:-translate-x-32 max-xl:[&_.text]:-translate-y-8 max-xl:[&_.text]:scale-125" />

            <svg
              width="1323"
              height="1327"
              viewBox="0 -199 1323 1327"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="pointer-events-none absolute -top-1/2 -left-1/2 -z-10 h-auto w-[200%]"
            >
              <g filter="url(#filter0_f_19713_18172)">
                <ellipse
                  cx="661.5"
                  cy="464.5"
                  rx="411.5"
                  ry="413.5"
                  fill="#18A0FB"
                  fillOpacity=".3"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_19713_18172"
                  x="0"
                  y="-199"
                  width="1323"
                  height="1327"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                  <feGaussianBlur result="effect1_foregroundBlur_19713_18172" stdDeviation="125" />
                </filter>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};
